import { useMemo } from 'react';
import type { Role } from '@/models';
import { selectUserRole } from '@/features/auth';
import { useAppSelector } from './redux';

interface Permissions {
  role: Role | null;
  canManageBatches: boolean;
  canAssignBuckets: boolean;
  canViewReports: boolean;
  canManageSettings: boolean;
  hasRole: (...roles: Role[]) => boolean;
}

function normalizeRole(raw: string | null): Role | null {
  if (!raw) return null;
  const r = raw.trim().toLowerCase();
  if (r === 'admin') return 'admin';
  if (r === 'agent' || r === 'ca' || r === 'hcc') return 'agent';
  if (r === 'supervisor' || r === 'ssf') return 'supervisor';
  return null;
}

export function usePermissions(): Permissions {
  const rawRole = useAppSelector(selectUserRole);
  const role = normalizeRole(rawRole);
  return useMemo(
    () => ({
      role,
      canManageBatches: role === 'admin' || role === 'supervisor',
      canAssignBuckets: role === 'admin' || role === 'supervisor',
      canViewReports: role !== null,
      canManageSettings: role === 'admin',
      hasRole: (...roles: Role[]) => role !== null && roles.includes(role),
    }),
    [role],
  );
}
